import React from 'react';
import { Container, Typography, Box, Divider, Button, Grid, Card, CardContent, Chip } from '@mui/material';
import { styled, keyframes } from '@mui/system';

// --- ICONS ---
import HubIcon from '@mui/icons-material/Hub';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import StorefrontIcon from '@mui/icons-material/Storefront';

// --- ANIMATIONS & STYLES ---
const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const Root = styled('div')({
    backgroundColor: '#101010',
    color: '#e0e0e0',
    minHeight: '100vh',
    padding: '4rem 2rem',
    animation: `${fadeIn} 1.2s ease-out`,
});

const Header = styled(Typography)({
    fontSize: '3.5rem',
    fontWeight: 700,
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: '1rem',
    '@media (max-width: 600px)': {
        fontSize: '2.5rem',
    },
});

const SubHeader = styled(Typography)({
    fontSize: '1.25rem',
    color: '#b0b0b0',
    maxWidth: '900px',
    textAlign: 'center',
    margin: '0 auto 5rem auto',
    lineHeight: 1.7,
});

const SectionTitle = styled(Typography)({
    fontSize: '2.5rem',
    fontWeight: 600,
    marginBottom: '2.5rem',
    color: '#ffffff',
    textAlign: 'center',
});

const PillarCard = styled(Card)({
    backgroundColor: 'rgba(31, 31, 31, 0.5)',
    border: '1px solid #2e2e2e',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    height: '100%',
    padding: '1rem',
    transition: 'transform 0.3s, border-color 0.3s',
    '&:hover': {
        borderColor: '#12c099',
        transform: 'scale(1.03)',
    }
});

const TechCategory = styled(Box)({
    marginBottom: '2rem',
    textAlign: 'center',
});

const TechLogo = styled('img')({
    height: '40px',
    margin: '0 1rem',
    filter: 'grayscale(100%) opacity(70%)',
    transition: 'all 0.3s ease',
    verticalAlign: 'middle',
    '&:hover': {
        filter: 'none',
        transform: 'scale(1.1)',
    }
});

const CTASection = styled(Box)({
    textAlign: 'center',
    padding: '4rem 2rem',
    backgroundColor: '#1f1f1f',
    borderRadius: '12px',
    margin: '5rem 0',
});

const Footer = styled('footer')({
    textAlign: 'center',
    padding: '2rem 0',
    color: '#888',
    borderTop: '1px solid #2e2e2e',
});

const EngineeringAndDataScience = () => {

    // ✅ FIX: All logo URLs have been verified and replaced with working links.
    const techStack = {
        "Frontend & Mobile": [
            { name: "React", url: "https://cdn.worldvectorlogo.com/logos/react-2.svg" },
            { name: "React Native", url: "https://cdn.worldvectorlogo.com/logos/react-native-1.svg" }
        ],
        "Backend & API": [
            { name: "Node.js", url: "https://cdn.worldvectorlogo.com/logos/nodejs-icon.svg" },
            { name: "Express", url: "https://cdn.worldvectorlogo.com/logos/express-109.svg" },
            { name: "Python", url: "https://cdn.worldvectorlogo.com/logos/python-5.svg" },
            { name: "Docker", url: "https://cdn.worldvectorlogo.com/logos/docker.svg" }
        ],
        "Database & Caching": [
            { name: "MongoDB", url: "https://cdn.worldvectorlogo.com/logos/mongodb-icon-1.svg" },
            { name: "Redis", url: "https://cdn.worldvectorlogo.com/logos/redis.svg" },
            { name: "PostgreSQL", url: "https://cdn.worldvectorlogo.com/logos/postgresql.svg" },
            { name: "Amazon Redshift", url: "https://cdn.worldvectorlogo.com/logos/amazon-redshift.svg" }
        ],
        "AI, ML & Quantum": [
            { name: "OpenAI", url: "https://cdn.worldvectorlogo.com/logos/openai-2.svg" },
            { name: "D-Wave", url: "https://upload.wikimedia.org/wikipedia/commons/4/4f/Logo_dwave.png" }
        ],
        "Cloud & DevOps": [
            { name: "AWS", url: "https://cdn.worldvectorlogo.com/logos/aws-logo.svg" },
            { name: "AWS Lambda", url: "https://cdn.worldvectorlogo.com/logos/aws-lambda.svg" },
            { name: "CloudFront", url: "https://cdn.freebiesupply.com/logos/large/2x/aws-cloudfront-logo-png-transparent.png" },
            { name: "Heroku", url: "https://cdn.worldvectorlogo.com/logos/heroku-4.svg" },
            { name: "Firebase", url: "https://cdn.worldvectorlogo.com/logos/firebase-1.svg" }
        ],
        "Geo & External Data": [
            { name: "Google Maps", url: "https://cdn.worldvectorlogo.com/logos/google-maps-2020-icon.svg" },
            { name: "Foursquare", url: "https://www.iconpacks.net/icons/2/free-foursquare-logo-icon-2445-thumb.png" },
            { name: "OpenCage", url: "https://assets.opencagedata.com/assets/opencage-logo.green-f1d1f310df885cbb4c3a0bc5b9eaeee7da15ecea55eb53affa390354fcc117e2.png" },
            { name: "Numbeo", url: "https://www.numbeo.com/images/numbeo_text_1B3B6F.svg" }
        ],
        "Payments & Comms": [
            { name: "Stripe", url: "https://cdn.worldvectorlogo.com/logos/stripe-4.svg" },
            { name: "PayPal", url: "https://cdn.worldvectorlogo.com/logos/paypal-3.svg" },
            { name: "Plaid", url: "https://upload.wikimedia.org/wikipedia/commons/b/bc/Plaid-logo.png" },
            { name: "Twilio", url: "https://cdn.worldvectorlogo.com/logos/twilio-2.svg" },
            { name: "SendGrid", url: "https://cdn.worldvectorlogo.com/logos/sendgrid-2.svg" }
        ]
    };

    return (
        <Root>
            <Container maxWidth="xl">
                <Header>Engineering the Future of In-Person Connection</Header>
                <SubHeader>
                    At United Sports, we are solving one of the most fundamental human challenges: orchestrating meaningful, real-world interactions at global scale. We are building a proprietary, AI-driven platform that autonomously manages the immense complexity of live events, starting with youth sports and expanding to define the future of human connection itself.
                </SubHeader>

                <SectionTitle>Our Engineering Pillars</SectionTitle>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                        <PillarCard>
                            <CardContent>
                                <HubIcon sx={{ fontSize: 40, color: '#12c099', mb: 2 }} />
                                <Typography variant="h5" color="white" fontWeight="600" gutterBottom>Autonomous Operations</Typography>
                                <Typography variant="body1" color="#c0c0c0">
                                    Our platform is powered by a network of custom and OpenAI-enabled agents that autonomously manage our application endpoints. These agents process real-time data from users and external APIs—from weather patterns to traffic data—to self-optimize event logistics, manage resources, and ensure seamless execution without human intervention.
                                </Typography>
                            </CardContent>
                        </PillarCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <PillarCard>
                            <CardContent>
                                <StorefrontIcon sx={{ fontSize: 40, color: '#12c099', mb: 2 }} />
                                <Typography variant="h5" color="white" fontWeight="600" gutterBottom>The Hyperlocal Commerce Engine</Typography>
                                <Typography variant="body1" color="#c0c0c0">
                                    We are building a dynamic, global advertising marketplace that bridges the digital-physical divide. Brands can sponsor access and transportation for users, targeting events by hyper-local demographics and measuring real-world ROI through check-in data, on-site impression tracking, and photographic confirmation of physical ad installations.
                                </Typography>
                            </CardContent>
                        </PillarCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <PillarCard>
                            <CardContent>
                                <TravelExploreIcon sx={{ fontSize: 40, color: '#12c099', mb: 2 }} />
                                <Typography variant="h5" color="white" fontWeight="600" gutterBottom>The Physical-World Logistics API</Typography>
                                <Typography variant="body1" color="#c0c0c0">
                                    Beyond software, we solve hard physical logistics. Our proprietary 3PL API orchestrates a network of facilities, contractors, and routing algorithms to manage the storage and transport of physical inventory—from custom banners to athlete apparel—creating an "Uber for event assets" that ensures the right equipment is at the right place, at the right time.
                                </Typography>
                            </CardContent>
                        </PillarCard>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 6, borderColor: '#2e2e2e' }} />

                <SectionTitle>The Technology We Use</SectionTitle>
                {Object.entries(techStack).map(([category, logos]) => (
                    <TechCategory key={category}>
                        <Chip label={category} sx={{ color: '#e0e0e0', borderColor: '#444', mb: 3, fontSize: '1rem' }} variant="outlined" />
                        <Box>
                            {logos.map(logo => (
                                <TechLogo key={logo.name} src={logo.url} alt={logo.name} title={logo.name} />
                            ))}
                        </Box>
                    </TechCategory>
                ))}
                <Typography sx={{ textAlign: 'center', color: '#888', mt: 3, fontStyle: 'italic' }}>
                    And we are actively exploring next-generation compute paradigms, including quantum-aware algorithms with partners like D-Wave, to solve complex optimization problems at unprecedented scale.
                </Typography>

                <CTASection>
                    <Typography variant="h4" color="white" fontWeight="600" gutterBottom>Solve Problems That Matter</Typography>
                    <Typography color="#c0c0c0" sx={{ mb: 3, maxWidth: '600px', margin: 'auto' }}>
                        If you are a first-principles thinker who wants to build systems that master real-world complexity and bring people together, you belong here.
                    </Typography>
                    <Button variant="contained" href="/careers" sx={{ backgroundColor: '#12c099', color: '#fff', padding: '12px 35px', fontSize: '1rem', mt: 3 }}>
                        View Open Engineering Roles
                    </Button>
                </CTASection>
            </Container>
            <Footer>
                © {new Date().getFullYear()} United Sports. All rights reserved.
            </Footer>
        </Root>
    );
};

export default EngineeringAndDataScience;